import React, {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import MessageBox, {MessageType} from "../components/MessageBox";
import {useSearchParams} from "react-router-dom";
import {FaLock, FaSearch} from "react-icons/fa";

import {Account} from "../../models/account";
import {AccountContactData} from "../../models/accountContactData";
import {AccountMedData} from "../../models/accountMedData";
import {EmergencyContact} from "../../models/emergencyContact";
import {getAccountByAccountCode} from "../../api/accountApi";

import {QUERY_PARAMS} from "../../constants/queryParams";
import {getSharedAccountContactDataById} from "../../api/accountContactDataApi";
import {getSharedAccountMedDataById} from "../../api/accountMedDataApi";
import {getSharedEmergencyContactsByAccount} from "../../api/emergencyContactApi";
import MedicalDataView from "../views/MedicalDataView";
import ContactDataView from "../views/ContactDataView";
import EmergencyContactsView from "../views/EmergencyContactsView";
import {useAccount} from "../../hooks/AccountContext";
import ViewLocked from "../components/ViewLocked";

interface ShowPersonProps {
}

function ShowPerson() {
    const {t} = useTranslation();
    const [searchParams, setSearchParams] = useSearchParams();

    const [message, setMessage] = useState<MessageType>(null);
    const [accountCode, setAccountCode] = useState<string>("");
    const [account, setAccount] = useState<Account | null>(null);
    const [accountContactData, setAccountContactData] = useState<AccountContactData | null>(null);
    const [accountMedData, setAccountMedData] = useState<AccountMedData | null>(null);
    const [emergencyContacts, setEmergencyContacts] = useState<EmergencyContact[]>([]);

    const notLoggedInMessage: MessageType = {
        type: "info",
        text: t("common.messages.notLoggedInMessage")
    };

    // 🔥 Globaler Account-State aus dem Context
    const {
        account: loggedInAccount,
    } = useAccount();

    // 1. URL beim Laden auslesen
    useEffect(() => {
        const urlCode = searchParams.get(QUERY_PARAMS.ACCOUNT);

        if (urlCode) {
            setAccountCode(urlCode);
            handleSearch(urlCode); // <-- direkt suchen, aber OHNE Event
        }
    }, []);

    const handleSearch = async (code?: string, e?: React.FormEvent) => {
        if (e) e.preventDefault();
        setMessage(null);

        const finalCode = code ?? accountCode;
        if (!finalCode) return;

        try {
            const account = await getAccountByAccountCode(finalCode);
            if (!account) {
                setMessage({type: "error", text: t("errors.accountNotFound")});
                setAccount(null);
                return;
            }

            setAccount(account);
            setSearchParams({[QUERY_PARAMS.ACCOUNT]: finalCode});

            const requesterId = loggedInAccount?.id ?? -1;

            const [contact, med, emergency] = await Promise.all([
                getSharedAccountContactDataById(requesterId, account.id),
                getSharedAccountMedDataById(requesterId, account.id),
                getSharedEmergencyContactsByAccount(requesterId, account.id)
            ]);

            setAccountContactData(contact);
            setAccountMedData(med);
            setEmergencyContacts(emergency);

        } catch {
            setMessage({type: "error", text: t("errors.accountNotFound")});
            setAccount(null);
        }
    };

    return (
        <div>
            <h2>{t("menu.showPerson")}</h2>

            <form onSubmit={(e) => handleSearch(accountCode, e)} className="form-container">
                <MessageBox message={message}/>

                <div className="form-group">
                    <div className="form-row split-85-15">
                        <input
                            type="text"
                            name="accountCode"
                            value={accountCode}
                            placeholder={t("contact.fields.accountCode")}
                            onChange={(e) => setAccountCode(e.target.value)}
                            className="form-input"
                            required
                        />
                        <button
                            type="submit"
                            disabled={accountCode.length !== 12}
                            className="form-button"
                        >
                            <FaSearch/>
                        </button>
                    </div>

                    {!loggedInAccount && (
                        <MessageBox
                            message={notLoggedInMessage}
                        />
                    )}
                </div>
            </form>

            {account &&
                <div>
                    {
                        (accountContactData || accountMedData || emergencyContacts.length > 0) ?
                            (
                                <div>
                                    <h3>{account.firstName} {account.lastName}</h3>
                                    <MedicalDataView medData={accountMedData}/>
                                    <EmergencyContactsView emergencyContacts={emergencyContacts}/>
                                    <ContactDataView contactData={accountContactData}/>
                                </div>
                            ) :
                            (
                                <ViewLocked message={t("common.messages.noSharedData")}/>
                            )
                    }
                </div>
            }
        </div>
    );
}

export default ShowPerson;